import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Button from '@amzn/meridian/button';
import Loader from '@amzn/meridian/loader';
import Alert from '@amzn/meridian/alert';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import Select, { SelectOption } from '@amzn/meridian/select';
import Input from '@amzn/meridian/input';
import { adminApi, BugReport } from '../service/adminApiService';
import { FilterBar } from '../components/FilterBar';
import { PaginationControls } from '../components/PaginationControls';
import { PageHeader } from '../components/PageHeader';
import { Section } from '../components/Section';

const CATEGORY_OPTIONS = ['', 'bug', 'feature_request', 'other'];
const SEVERITY_OPTIONS = ['', 'low', 'medium', 'high'];

/**
 * Read-only admin listing of user-submitted bug/issue reports
 * (spec ordis-bug-report). Modeled on AuditLogPage: paginated table with
 * client-driven filters kept in the URL query string.
 */
export const BugReportsPage: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState<BugReport[]>([]);
  const [tokens, setTokens] = useState<(string | null)[]>([null]);
  const [pageIndex, setPageIndex] = useState(0);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const user = params.get('user') || '';
  const category = params.get('category') || '';
  const severity = params.get('severity') || '';
  const status = params.get('status') || '';
  const from = params.get('from') || '';
  const to = params.get('to') || '';

  const setParam = useCallback(
    (key: string, value: string | null) => {
      const next = new URLSearchParams(params);
      if (value) next.set(key, value);
      else next.delete(key);
      setParams(next, { replace: true });
      setPageIndex(0);
      setTokens([null]);
    },
    [params, setParams],
  );

  const clearFilters = useCallback(() => {
    setParams({}, { replace: true });
    setPageIndex(0);
    setTokens([null]);
  }, [setParams]);

  const fetchPage = useCallback(
    async (tokenIndex: number) => {
      setLoading(true);
      setError(null);
      try {
        const token = tokens[tokenIndex] || undefined;
        const response = await adminApi.listBugReports({
          next_token: token,
          user: user || undefined,
          category: category || undefined,
          severity: severity || undefined,
          status: status || undefined,
          from: from || undefined,
          to: to || undefined,
        });
        setItems(response.items);
        setNextToken(response.next_token);
      } catch (err: any) {
        setError(err?.message || 'Failed to load bug reports');
        setItems([]);
      } finally {
        setLoading(false);
      }
    },
    [tokens, user, category, severity, status, from, to],
  );

  useEffect(() => {
    fetchPage(pageIndex);
  }, [fetchPage, pageIndex]);

  const activeCount = useMemo(
    () => [user, category, severity, status, from, to].filter((v) => v && v.length > 0).length,
    [user, category, severity, status, from, to],
  );

  return (
    <Column spacing="large">
      <PageHeader
        title="Bug reports"
        subtitle="User-submitted bug, feature-request, and issue reports from the chat UI."
      />

      <Section>
        <FilterBar activeCount={activeCount} onClear={clearFilters}>
          <div style={{ minWidth: 180 }}>
            <Input
              type="text"
              value={user}
              onChange={(v: string) => setParam('user', v || null)}
              placeholder="Reporter alias"
              aria-label="Filter by reporter"
            />
          </div>
          <Select
            value={category}
            onChange={(v: string) => setParam('category', v || null)}
            placeholder="Type: any"
            aria-label="Filter by type"
            width={200}
          >
            {CATEGORY_OPTIONS.map((c) => (
              <SelectOption key={c || 'any'} value={c} label={c ? c.replace('_', ' ') : 'Any'} />
            ))}
          </Select>
          <Select
            value={severity}
            onChange={(v: string) => setParam('severity', v || null)}
            placeholder="Severity: any"
            aria-label="Filter by severity"
            width={180}
          >
            {SEVERITY_OPTIONS.map((s) => (
              <SelectOption key={s || 'any'} value={s} label={s || 'Any'} />
            ))}
          </Select>
          <div style={{ minWidth: 160 }}>
            <Input
              type="text"
              value={status}
              onChange={(v: string) => setParam('status', v || null)}
              placeholder="Status (e.g. NEW)"
              aria-label="Filter by status"
            />
          </div>
        </FilterBar>

        {error && <Alert type="error">{error}</Alert>}

        {loading ? (
          <Row alignmentHorizontal="center" spacingInset="500">
            <Loader type="circular" size="large" />
          </Row>
        ) : items.length === 0 ? (
          <Alert type="neutral">No bug reports match the current filters.</Alert>
        ) : (
          <Column spacing="none">
            <Table headerRows={1} showDividers={true} aria-label="Bug reports">
              <TableRow>
                <TableCell>Created</TableCell>
                <TableCell>Reporter</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Severity</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Details</TableCell>
              </TableRow>
              {items.map((r) => (
                <TableRow key={r.report_id}>
                  <TableCell>
                    <Text type="b200">{r.created_at}</Text>
                  </TableCell>
                  <TableCell>{r.user_alias}</TableCell>
                  <TableCell>{r.category}</TableCell>
                  <TableCell>{r.severity}</TableCell>
                  <TableCell>{r.title}</TableCell>
                  <TableCell>{r.status}</TableCell>
                  <TableCell>
                    <Button
                      type="tertiary"
                      size="small"
                      onClick={() => setExpanded(expanded === r.report_id ? null : r.report_id)}
                    >
                      {expanded === r.report_id ? 'Hide' : 'Show'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </Table>
            {expanded && (
              <Column spacingInset="400" spacing="200">
                {items
                  .filter((r) => r.report_id === expanded)
                  .map((r) => (
                    <Column key={r.report_id} spacing="100">
                      <Text type="b100" color="secondary">
                        {r.report_id} · session {r.session_id || 'n/a'} · stage {r.stage || 'n/a'}
                      </Text>
                      <Text>{r.description}</Text>
                      {r.page_url && (
                        <Text type="b100" color="secondary">
                          Page: {r.page_url}
                        </Text>
                      )}
                      {r.user_agent && (
                        <Text type="b100" color="secondary">
                          UA: {r.user_agent}
                        </Text>
                      )}
                    </Column>
                  ))}
              </Column>
            )}
          </Column>
        )}

        <PaginationControls
          currentIndex={pageIndex}
          hasPrevious={pageIndex > 0}
          hasNext={!!nextToken}
          onPrevious={() => setPageIndex(pageIndex - 1)}
          onNext={() => {
            if (!nextToken) return;
            const newTokens = [...tokens];
            newTokens[pageIndex + 1] = nextToken;
            setTokens(newTokens);
            setPageIndex(pageIndex + 1);
          }}
          itemCount={items.length}
        />
      </Section>
    </Column>
  );
};
